import { Box, Grid, Stack, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ActionButton,
  Breadcrumbs,
  configureFlowQuery,
  FlowResourceType,
  HeaderWithScrollables,
  PageLayout,
  PageNames,
  useCreateAssetTypeActions,
  useEntities,
  useEnvironment,
  entitiesQuery,
  useLocationTrackingForThingsGroup,
} from '@diana/core';
import { ThingsGroupCard } from './ThingsGroupCard';
import { ThingsGroupDraftCard } from './ThingsGroupDraftCard';
import { ThingsGroupPageEmptyView } from './ThingsGroupPageEmptyView';
import { useBreadcrumbsRoot } from '../../hooks/useBreadcrumbsRoot';
import { THINGS_GROUP_PAGE_NS } from './i18n';

export const ThingsGroupsPage = () => {
  const [isProcessing, setProcessing] = useState(false);
  const { t } = useTranslation(THINGS_GROUP_PAGE_NS);
  const environmentName = useEnvironment();
  const onFetchFinished = useCallback(() => setProcessing(false), []);

  const createAssetType = useCreateAssetTypeActions();

  const {
    data: entities,
    isLoading,
    refetch,
  } = useEntities({
    onSuccess: onFetchFinished,
    onError: onFetchFinished,
  });

  const draftsQuery = configureFlowQuery.searchDraft({
    environmentName,
    flowResourceType: FlowResourceType.Entity,
  });

  const totalCountQuery = entitiesQuery.assetCount({});

  const {
    data: draftsData,
    isLoading: isLoadingDrafts,
    refetch: refetchDrafts,
  } = useQuery({
    queryKey: draftsQuery.key,
    queryFn: draftsQuery.fn,
    enabled: Boolean(environmentName),
    staleTime: 0,
  });

  const { data: countData, isFetching: isFetchingCounts } = useQuery({
    queryKey: totalCountQuery.key,
    queryFn: totalCountQuery.fn,
    enabled: Boolean(environmentName),
    staleTime: 0,
  });

  const isContentEmpty = [!entities?.length, !draftsData?.length].every((v) => v);

  const isContentLoading = [
    isProcessing,
    isLoading,
    isLoadingDrafts && draftsData === undefined,
    createAssetType.isCreatingFlow,
  ].some((v) => v);

  useLocationTrackingForThingsGroup({ trackOnMount: true });
  const breadcrumbRoot = useBreadcrumbsRoot('assets');
  return (
    <PageLayout
      name={PageNames.THING_TYPES}
      title={breadcrumbRoot.title}
      isContentLoading={isContentLoading}
      header={
        <HeaderWithScrollables
          left={<Breadcrumbs items={[breadcrumbRoot]} />}
          right={
            !!createAssetType.actions.length &&
            !isContentLoading && (
              <ActionButton
                actions={createAssetType.actions}
                preventUseMonoActionIcon
                sx={({ spacing }) => ({ height: spacing(4) })}
              >
                {createAssetType.displayName}
              </ActionButton>
            )
          }
        />
      }
    >
      {(() => {
        if (isContentEmpty && !isContentLoading) {
          return <ThingsGroupPageEmptyView />;
        }

        return (
          <Stack direction="column" spacing={2}>
            {!!entities?.length && (
              <Box>
                <Grid container rowSpacing={2} spacing={2}>
                  {entities?.map((entity) => {
                    const originId = entity.flow?.flowOriginId;
                    const countAssetData = originId && countData ? countData[originId] : null;
                    return (
                      <ThingsGroupCard
                        key={entity.flow?.flowOriginId}
                        entity={entity}
                        totalCount={countAssetData}
                        isFetching={isFetchingCounts}
                        refetch={refetch}
                        setProcessing={setProcessing}
                      />
                    );
                  })}
                </Grid>
              </Box>
            )}
            {!!draftsData?.length && (
              <Box>
                <Typography color="text.secondary" mb={2}>
                  {t('titles.drafts')}
                </Typography>
                <Grid container rowSpacing={2} spacing={2}>
                  {draftsData.map((draftFlow) => (
                    <ThingsGroupDraftCard
                      key={draftFlow.flowOriginId}
                      flow={draftFlow}
                      refetch={refetchDrafts}
                      setProcessing={setProcessing}
                    />
                  ))}
                </Grid>
              </Box>
            )}
          </Stack>
        );
      })()}
      {createAssetType.content}
    </PageLayout>
  );
};
